import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import type { MySubscriptionStoredData } from '../helpers/storageUtils';
import { ADMIN_HUB_TABS, ADMIN_HUB_URL_PARAMS, DISABLE_SUBSCRIPTION } from '../helpers/constants';

/**
 * Formats an ISO date string (e.g. "2026-02-28T00:00:00") to a readable date like "February 28, 2026".
 */
function formatEndDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

interface SubscriptionCancellationBannerProps {
  /** Subscription data from mySubscription API (subscriptionStatus, endDate). */
  subscriptionData: MySubscriptionStoredData | null;
}

/**
 * Banner shown at the top of the app when subscription is cancelled (subscriptionStatus === 'inactive').
 * Displays cancellation message with end date and a "Reactivate Subscription" action.
 */
const SubscriptionCancellationBanner: React.FC<SubscriptionCancellationBannerProps> = ({
  subscriptionData,
}) => {
  const navigate = useNavigate();

  const isInactive =
    subscriptionData?.subscriptionStatus?.toLowerCase() === 'inactive';
  const endDate = subscriptionData?.endDate;

  if (!isInactive || !endDate) {
    return null;
  }

  const formattedDate = formatEndDate(endDate);

  /** Open Admin Hub on Subscription Management with Manage Plan modal (deep link via query params) */
  const handleReactivate = () => {
    if (DISABLE_SUBSCRIPTION) {
      navigate('/adminHub');
      return;
    }
    const params = new URLSearchParams({
      [ADMIN_HUB_URL_PARAMS.TAB]: ADMIN_HUB_TABS.SUBSCRIPTION_MANAGEMENT.value,
      [ADMIN_HUB_URL_PARAMS.MANAGE_PLAN]: '1',
    });
    navigate(`/adminHub?${params.toString()}`);
  };

  return (
    <Box
      sx={{
        width: '100%',
        py: 1.5,
        px: 2,
        backgroundColor: '#ed6c02',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 1,
        boxShadow: 1,
        borderRadius: 0,
      }}
    >
      <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
        Your subscription has been cancelled. You will lose access on{' '}
        {formattedDate}.
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={handleReactivate}
        sx={{
          backgroundColor: 'rgba(255,255,255,0.95)',
          color: '#ed6c02',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#fff',
          },
        }}
      >
        Reactivate Subscription →
      </Button>
    </Box>
  );
};

export default SubscriptionCancellationBanner;
